import { z } from 'zod';

// What a collection page, the all-products page and the search page ask the catalogue for, and the facts
// their filters are drawn from. One shape for all three, because they are one page with three addresses:
// a set of products narrowed by the shopper, sorted, and paged by number as Sleek pages them.

/** The orders a shopper may sort a listing by. Relevance only means something for a search. */
export const CATALOG_SORTS = ['manual', 'best-selling', 'price-asc', 'price-desc', 'newest', 'title-asc', 'title-desc', 'relevance'] as const;
export const CatalogSortSchema = z.enum(CATALOG_SORTS);

/** The most cards one page may ask for, and how many a page holds when it does not say. */
export const CATALOG_MAX_PAGE = 48;
export const CATALOG_DEFAULT_PAGE = 15;

/** How far a shopper may page before the listing asks them to narrow it instead. */
const MAX_PAGE_NUMBER = 500;

// Reads a comma-separated list from the address, keeping at most ten short, distinct, non-empty values.
const listParam = z.string().max(600).optional()
  .transform((raw) => [...new Set((raw ?? '').split(',').map((value) => value.trim()).filter(Boolean))].slice(0, 10).map((value) => value.slice(0, 100)));

/** One listing request: which products, which narrowing, which order, which page. */
export const CatalogBrowseQuerySchema = z.object({
  collection: z.string().trim().max(255).optional(),
  q: z.string().trim().max(100).optional(),
  sort: CatalogSortSchema.optional(),
  available: z.enum(['true', 'false']).optional().transform((value) => value === 'true'),
  priceMin: z.coerce.number().min(0).max(10_000_000).optional(),
  priceMax: z.coerce.number().min(0).max(10_000_000).optional(),
  type: listParam,
  vendor: listParam,
  option: listParam,
  page: z.coerce.number().int().min(1).max(MAX_PAGE_NUMBER).default(1),
  limit: z.coerce.number().int().min(1).max(CATALOG_MAX_PAGE).default(CATALOG_DEFAULT_PAGE)
});

/** One value a filter offers, and how many products in the listing carry it. */
export const FacetValueSchema = z.object({ value: z.string(), count: z.number() });

/** What the filters are drawn from: the listing before the shopper narrowed it. */
export const CatalogFacetsSchema = z.object({
  price: z.object({ min: z.number(), max: z.number() }),
  inStock: z.number(),
  outOfStock: z.number(),
  types: z.array(FacetValueSchema),
  vendors: z.array(FacetValueSchema),
  options: z.array(z.object({ name: z.string(), values: z.array(FacetValueSchema) }))
});

/** The collection a listing belongs to, when it belongs to one. */
export const BrowsedCollectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  descriptionHtml: z.string().nullable(),
  imageUrl: z.string().nullable()
});

/** One listing page as the storefront receives it; each card's own fields are read by the card. */
export const CatalogPageSchema = z.object({
  collection: BrowsedCollectionSchema.nullable(),
  items: z.array(z.object({ id: z.string(), title: z.string(), slug: z.string() }).passthrough()),
  total: z.number(),
  page: z.number(),
  pages: z.number(),
  sort: CatalogSortSchema,
  facets: CatalogFacetsSchema
});

export type CatalogSort = z.infer<typeof CatalogSortSchema>;
export type CatalogPageDto = z.infer<typeof CatalogPageSchema>;
export type CatalogBrowseQuery = z.infer<typeof CatalogBrowseQuerySchema>;
export type FacetValue = z.infer<typeof FacetValueSchema>;
export type CatalogFacets = z.infer<typeof CatalogFacetsSchema>;
export type BrowsedCollection = z.infer<typeof BrowsedCollectionSchema>;
